import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, RotateCcw, CheckCircle2, XCircle, AlertTriangle, Info } from "lucide-react";
import CTAButtons from "@/components/CTAButtons";

// Section 12E of the Income Tax Act — Small Business Corporation criteria.
// Each question returns one of: "yes" | "no" | "maybe" with explanation.
const QUESTIONS = [
    {
        id: "entity",
        question: "What type of entity is the business?",
        helper: "SBC status under s.12E is only available to specific entity types.",
        options: [
            { label: "Private company (Pty) Ltd", value: "ok" },
            { label: "Close Corporation (CC)", value: "ok" },
            { label: "Co-operative", value: "ok" },
            { label: "Personal Liability Company (Inc)", value: "ok" },
            { label: "Sole proprietor / partnership", value: "fail" },
            { label: "Trust", value: "fail" },
            { label: "Public company / NPC", value: "fail" },
        ],
        evaluate: (v) =>
            v === "fail"
                ? { kind: "fail", reason: "SBC status is only available to private companies, close corporations, co-operatives and personal liability companies. Sole proprietors, partnerships, trusts and public companies are excluded." }
                : { kind: "ok" },
    },
    {
        id: "shareholders",
        question: "Are ALL of the company's shareholders / members natural persons (real human beings) — and have they been throughout the entire year of assessment?",
        helper: "If any shareholder is another company, a trust, or a juristic person, the entity disqualifies — with very narrow exceptions.",
        options: [
            { label: "Yes — every shareholder is a natural person", value: "ok" },
            { label: "No — at least one shareholder is a trust or company", value: "fail" },
            { label: "Mixed during the year (changed)", value: "maybe" },
        ],
        evaluate: (v) => {
            if (v === "fail")
                return { kind: "fail", reason: "Section 12E requires that every shareholder / member be a natural person at all times during the year of assessment. There are limited carve-outs (e.g. body corporates, dormant companies, share-block companies, listed companies, REITs, friendly societies, PBOs, VCC) — please confirm your facts with us." };
            if (v === "maybe")
                return { kind: "maybe", reason: "If there was a non-natural shareholder at any point during the year, SBC status is jeopardised for that full year. The detail of when, and what type of shareholder, matters — let TC Auditors review." };
            return { kind: "ok" };
        },
    },
    {
        id: "otherShares",
        question: "Do any of your shareholders / members hold shares (or a member's interest) in ANY other private company, CC or co-operative?",
        helper: "Holding shares in OTHER private entities is a classic SBC disqualifier — but several exceptions apply.",
        options: [
            { label: "No — none of us hold shares in any other private entity", value: "ok" },
            { label: "Yes — but only in listed companies / REITs / PBOs / dormant companies / body corporates / share-block companies / VCCs", value: "ok_exception" },
            { label: "Yes — in another active private company / CC", value: "fail" },
            { label: "Not sure", value: "maybe" },
        ],
        evaluate: (v) => {
            if (v === "fail")
                return { kind: "fail", reason: "If any shareholder holds shares in another active private company, CC or co-operative, the SBC qualification falls away. There are exceptions for listed companies, REITs, PBOs, share-block companies, body corporates, dormant companies and Venture Capital Companies." };
            if (v === "maybe")
                return { kind: "maybe", reason: "We can review each shareholder's investment portfolio against the s.12E exceptions and tell you exactly where you stand." };
            return { kind: "ok" };
        },
    },
    {
        id: "income",
        question: "Is more than 20% of the company's total receipts and accruals derived from 'investment income' (interest, dividends, royalties, rentals from immovable property, annuities, foreign currency gains) AND 'personal service income' combined?",
        helper: "SBCs are meant to be active trading businesses. Companies whose income is mostly passive or personal-service in nature do not qualify.",
        options: [
            { label: "No — most income is from active trade", value: "ok" },
            { label: "Yes — more than 20% is investment / personal-service income", value: "fail" },
            { label: "Borderline — close to 20%", value: "maybe" },
        ],
        evaluate: (v) => {
            if (v === "fail")
                return { kind: "fail", reason: "Where >20% of receipts and accruals are investment income and / or income from rendering a personal service, the company does not qualify as an SBC. Please book a consultation — there are sometimes practical structuring options." };
            if (v === "maybe")
                return { kind: "maybe", reason: "When you are close to the 20% threshold, the precise classification of every receipt matters. We can run the percentage with you." };
            return { kind: "ok" };
        },
    },
    {
        id: "personalService",
        question: "Is the company's main business the rendering of a 'personal service' (defined in s.12E as accounting, actuarial, auctioneering, broadcasting, consulting, draughtsmanship, education, engineering, financial services, health, information technology, journalism, law, management, performing arts, real estate, research, secretarial services, sport, translation, valuation or veterinary services)?",
        helper: "Personal-service businesses are excluded UNLESS the company employs three or more full-time, unconnected employees engaged in that business throughout the year.",
        options: [
            { label: "No — not a personal-service business", value: "ok" },
            { label: "Yes, but we employ 3+ full-time employees who are not connected to any shareholder", value: "ok_exception" },
            { label: "Yes — and we have fewer than 3 unconnected full-time employees", value: "fail" },
        ],
        evaluate: (v) => {
            if (v === "fail")
                return { kind: "fail", reason: "A personal-service business with fewer than 3 unconnected full-time employees engaged in the business throughout the year is excluded from SBC status, regardless of size." };
            return { kind: "ok" };
        },
    },
    {
        id: "turnover",
        question: "Is the company's gross income for the year of assessment R20 million or less?",
        helper: "Gross income — not turnover or profit. Includes most receipts and accruals.",
        options: [
            { label: "Yes — R20m or less", value: "ok" },
            { label: "No — gross income exceeds R20m", value: "fail" },
            { label: "Not sure", value: "maybe" },
        ],
        evaluate: (v) => {
            if (v === "fail")
                return { kind: "fail", reason: "The SBC gross-income cap is R20 million. Above this amount, the standard 27% corporate rate applies." };
            if (v === "maybe")
                return { kind: "maybe", reason: "If you are unsure, share your trial balance with us and we will calculate gross income per the s.1 definition." };
            return { kind: "ok" };
        },
    },
];

export default function SBCQualifier() {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});

    const result = useMemo(() => {
        const evals = QUESTIONS.map((q) => (answers[q.id] !== undefined ? q.evaluate(answers[q.id]) : null));
        const fails = evals.filter((e) => e && e.kind === "fail");
        const maybes = evals.filter((e) => e && e.kind === "maybe");
        if (Object.keys(answers).length < QUESTIONS.length) return null;
        if (fails.length) return { kind: "fail", details: fails };
        if (maybes.length) return { kind: "maybe", details: maybes };
        return { kind: "pass", details: [] };
    }, [answers]);

    const total = QUESTIONS.length;
    const current = QUESTIONS[step];
    const finished = step >= total;

    const choose = (val) => {
        setAnswers((a) => ({ ...a, [current.id]: val }));
        setStep((s) => s + 1);
    };

    const reset = () => {
        setAnswers({});
        setStep(0);
    };

    return (
        <QuestionnairePage
            title="Small Business Corporation (SBC) Qualifier"
            subtitle="Section 12E of the Income Tax Act"
            current={step}
            total={total}
            onReset={reset}
            testIdRoot="sbc"
        >
            {!finished && current && (
                <div className="space-y-7" data-testid={`sbc-question-${step}`}>
                    <div>
                        <p className="overline text-tca-orange mb-3">Question {step + 1} of {total}</p>
                        <h2 className="font-display font-extrabold text-tca-ink text-2xl sm:text-3xl leading-tight">
                            {current.question}
                        </h2>
                        {current.helper && (
                            <p className="mt-3 text-sm text-tca-mute leading-relaxed flex items-start gap-2">
                                <Info className="w-4 h-4 mt-0.5 text-tca-orange shrink-0" /> {current.helper}
                            </p>
                        )}
                    </div>
                    <div className="space-y-2.5">
                        {current.options.map((o) => (
                            <button
                                key={o.label}
                                onClick={() => choose(o.value)}
                                data-testid={`sbc-option-${step}-${o.value}`}
                                className="w-full text-left bg-white border border-tca-line hover:border-tca-orange hover:bg-tca-orangeSoft/50 transition-all px-5 py-4 group flex items-center justify-between gap-3"
                            >
                                <span className="font-medium text-tca-ink">{o.label}</span>
                                <ArrowRight className="w-4 h-4 text-tca-mute group-hover:text-tca-orange transition-colors" />
                            </button>
                        ))}
                    </div>
                    {step > 0 && (
                        <button
                            onClick={() => setStep(step - 1)}
                            data-testid={`sbc-back-${step}`}
                            className="btn-ghost"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                    )}
                </div>
            )}

            {finished && result && (
                <ResultPanel
                    result={result}
                    passTitle="You may qualify as a Small Business Corporation"
                    passBody="Based on your answers, the company appears to satisfy each leg of the s.12E test. SBC status grants a graduated tax rate (lower than 27%) and accelerated wear-and-tear under s.12E(3). Confirm with TC Auditors before relying on this for a tax return — the legislation is technical and SARS audits SBC claims closely."
                    passExtra={
                        <div className="text-sm text-tca-ink leading-relaxed space-y-3">
                            <p>If the company meets all the above criteria, it is eligible for the following relief from SARS:</p>
                            <ul className="space-y-3">
                                <li>
                                    <strong>Accelerated Depreciation (Section 12E):</strong> The business can write off 100% of the cost of manufacturing plant and machinery in the year it is brought into use. Qualifying non-manufacturing assets can be depreciated on a fast-tracked 50/30/20 schedule over three years.
                                </li>
                                <li>
                                    <strong>Progressive Tax Rates (Years of assessment ending 1 April 2026 – 31 March 2027):</strong> Instead of paying the flat 27% corporate rate from the first Rand of profit, SBCs are taxed on a sliding scale:
                                    <ul className="mt-2 space-y-1 pl-4 border-l-2 border-emerald-300">
                                        <li><strong>R0 – R99,000:</strong> 0% tax</li>
                                        <li><strong>R99,001 – R365,000:</strong> 7% of the amount above R99,000</li>
                                        <li><strong>R365,001 – R550,000:</strong> R18,620 + 21% of the amount above R365,000</li>
                                        <li><strong>R550,001 and above:</strong> R57,470 + 27% of the amount above R550,000</li>
                                    </ul>
                                </li>
                            </ul>
                        </div>
                    }
                    failTitle="The company does not appear to qualify as an SBC this year"
                    failBody="One or more disqualifiers came up:"
                    maybeTitle="Some borderline answers — let's review"
                    maybeBody="A few of your answers fall in grey areas where the precise facts decide the outcome:"
                    onReset={reset}
                />
            )}
        </QuestionnairePage>
    );
}

// =====================================================================
// Shared Questionnaire shell + result components — kept in this file to
// avoid creating utilities that aren't reused elsewhere.
// =====================================================================
export function QuestionnairePage({ title, subtitle, current, total, onReset, children, testIdRoot }) {
    const pct = Math.min(100, Math.round((current / total) * 100));
    const handleReset = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        onReset();
    };
    return (
        <div className="bg-tca-fog min-h-screen" data-testid={`page-${testIdRoot}`}>
            <div className="bg-white border-b border-tca-line">
                <div className="container-tca py-4 flex items-center justify-between gap-3">
                    <Link to="/checklists" className="btn-ghost text-base gap-2.5" data-testid="back-to-checklists">
                        <ArrowLeft className="w-5 h-5" /> All checklists
                    </Link>
                    <button onClick={handleReset} className="btn-ghost" data-testid={`${testIdRoot}-restart`}>
                        <RotateCcw className="w-4 h-4" /> Restart
                    </button>
                </div>
            </div>

            <section className="container-tca py-12 md:py-16">
                <div className="max-w-3xl mx-auto">
                    <div className="mb-8">
                        <p className="overline mb-3">{subtitle}</p>
                        <h1 className="font-display font-extrabold text-tca-ink text-4xl sm:text-5xl leading-tight">{title}</h1>
                    </div>

                    {/* Progress */}
                    <div className="mb-8 h-1 bg-tca-line w-full" data-testid={`${testIdRoot}-progress`}>
                        <div className="h-full bg-tca-orange transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>

                    <div className="bg-white border border-tca-line p-7 sm:p-10">{children}</div>
                </div>
            </section>
        </div>
    );
}

export function ResultPanel({ result, passTitle, passBody, failTitle, failBody, maybeTitle, maybeBody, passExtra, onReset }) {
    const map = {
        pass: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-300", label: "Likely qualifies", title: passTitle, body: passBody },
        fail: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", border: "border-red-300", label: "Does not qualify", title: failTitle, body: failBody },
        maybe: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-300", label: "Borderline", title: maybeTitle, body: maybeBody },
    };
    const r = map[result.kind];
    const Icon = r.icon;
    const handleReset = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        onReset();
    };
    return (
        <div data-testid="questionnaire-result">
            <div className={`flex items-start gap-4 p-5 ${r.bg} border ${r.border} mb-6`}>
                <Icon className={`w-7 h-7 ${r.color} shrink-0 mt-0.5`} />
                <div className="flex-1">
                    <p className={`overline ${r.color} mb-1.5`}>{r.label}</p>
                    <h3 className="font-display font-extrabold text-tca-ink text-xl mb-2">{r.title}</h3>
                    <p className="text-sm text-tca-ink leading-relaxed">{r.body}</p>
                    {result.kind === "pass" && passExtra && (
                        <div className="mt-4 pt-4 border-t border-emerald-300">
                            {passExtra}
                        </div>
                    )}
                </div>
            </div>

            {result.details && result.details.length > 0 && (
                <ul className="space-y-3 mb-6">
                    {result.details.map((d, i) => (
                        <li key={i} className="border-l-4 border-tca-orange pl-4 text-sm text-tca-mute leading-relaxed" data-testid={`result-detail-${i}`}>
                            {d.reason}
                        </li>
                    ))}
                </ul>
            )}

            <div className="bg-tca-orangeSoft/40 border border-tca-orange p-5 mb-6">
                <p className="font-display font-bold text-tca-ink mb-1">Want certainty?</p>
                <p className="text-sm text-tca-ink leading-relaxed mb-4">
                    This tool answers the principle. Your specific facts can change the result — and the cost of getting it wrong is real (back-tax + penalties). Book a 30-minute virtual meeting and a CA(SA) will run it with you.
                </p>
                <CTAButtons testIdPrefix="questionnaire-result-cta" />
            </div>

            <button onClick={handleReset} className="btn-ghost" data-testid="questionnaire-restart">
                <RotateCcw className="w-4 h-4" /> Take it again
            </button>
        </div>
    );
}
